import React, { useEffect, useRef, useState, useMemo } from 'react';
import dayjs from 'dayjs';
import { cn } from '../../lib/utils';
import { Calendar } from './Calendar';
import { Calendar as CalendarIcon } from '../../assets/Calendar';
import { SypacInput } from '@sypac/component-library-react';
import 'dayjs/locale/en';
import 'dayjs/locale/pl';
import 'dayjs/locale/ro';
import 'dayjs/locale/ru';
import { DatePickerProps } from './DatePicker.interface';
import { AnimatePresence, motion } from 'framer-motion';
import {
	useFloating,
	autoUpdate,
	offset,
	flip,
	shift,
	useInteractions,
	useRole,
	FloatingPortal,
	useId,
} from '@floating-ui/react';

export function DatePicker({
	className,
	placeholder = 'Select date',
	value,
	onChange,
	min,
	max,
	allowOtherMonthSelection,
	locale,
	error,
	container,
	calendarPosition = 'right',
}: DatePickerProps) {
	dayjs.locale(locale || 'en');
	const [date, setDate] = useState<string | undefined>(value);
	const [isOpen, setIsOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const calendarRef = useRef<HTMLDivElement>(null);

	const [validContainer, setValidContainer] = useState<HTMLElement | null>(
		null,
	);

	useEffect(() => {
		if (container) {
			setValidContainer(container);
			return;
		}

		if (containerRef.current) {
			const modalOrSidebarRoot = containerRef.current.closest(
				'.modal-inside,.modal-overlay,[data-modal-root],[data-sidebar-root],[role="dialog"]',
			) as HTMLElement | null;

			setValidContainer(modalOrSidebarRoot || document.body);
		} else {
			setValidContainer(document.body);
		}
	}, [container]);

	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		placement: 'bottom',
		strategy: 'fixed',
		middleware: [
			offset({ mainAxis: 8 }),
			flip({ fallbackPlacements: ['top'] }),
			shift({ padding: 8 }),
		],
		whileElementsMounted: autoUpdate,
	});

	const role = useRole(context);
	const { getReferenceProps, getFloatingProps } = useInteractions([role]);

	const headingId = useId();
	const floatingProps = getFloatingProps();

	const inputDate = useMemo(() => {
		const value = date
			? dayjs(date).format('DD MMM, YYYY').replace('.', '')
			: '';

		return value.replace(
			/(\d{2}) (\p{L}+), (\d{4})/u,
			(_, day, month, year) => {
				const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1);
				return `${day} ${capitalizedMonth}, ${year}`;
			},
		);
	}, [date]);

	const handleDateChange = (selectedDate: Date) => {
		const formatted = dayjs(selectedDate)
			.format('DD MMM, YYYY')
			.replace('.', '');
		setDate(formatted);
		onChange(formatted);
		setIsOpen(false);
	};

	const handleCalendarInteraction = (e: React.MouseEvent) => {
		e.stopPropagation();
		e.nativeEvent?.stopImmediatePropagation?.();
	};

	useEffect(() => {
		if (!isOpen) return;

		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as Node;

			const clickedOutside =
				!containerRef.current?.contains(target) &&
				!calendarRef.current?.contains(target);

			const isInModal = !!(target as HTMLElement).closest(
				'[data-modal-root], .modal, #datepicker-modal',
			);

			if (clickedOutside && !isInModal) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen]);

	useEffect(() => {
		setDate(value);
	}, [value]);

	return (
		<div ref={containerRef} className={cn('relative', className)}>
			<div ref={refs.setReference} {...getReferenceProps()}>
				<SypacInput className="relative" onClick={() => setIsOpen(!isOpen)}>
					<input
						type="text"
						placeholder={placeholder}
						value={inputDate}
						readOnly
						className={cn(
							'py-2.5 border rounded-lg placeholder:text-gray-22',
							error ? 'border-red-orange' : '',
							calendarPosition === 'right'
								? 'order-1 pl-3'
								: 'order-2 pl-[35px]',
						)}
						aria-expanded={isOpen}
						aria-haspopup="dialog"
						aria-controls={isOpen ? headingId : undefined}
					/>
					<span
						className={cn(
							'absolute',
							calendarPosition === 'right'
								? 'order-2 top-3 right-4'
								: 'order-1 top-[11px] left-3',
						)}
					>
						<CalendarIcon />
					</span>
				</SypacInput>
			</div>

			{isOpen && validContainer && (
				<FloatingPortal root={validContainer}>
					<div
						ref={(node) => {
							refs.setFloating(node);
							// @ts-ignore
							calendarRef.current = node;
						}}
						style={{
							...floatingStyles,
							zIndex: 9999,
						}}
						{...floatingProps}
						onMouseDown={(e) => {
							handleCalendarInteraction(e);
							// @ts-ignore – in case floatingProps has no onMouseDown
							floatingProps.onMouseDown?.(e);
						}}
						onClick={(e) => {
							handleCalendarInteraction(e);
							// @ts-ignore – in case floatingProps has no onClick
							floatingProps.onClick?.(e);
						}}
					>
						<AnimatePresence>
							<motion.div
								initial={{ opacity: 0, y: -10 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -10 }}
								transition={{ duration: 0.2 }}
								className="shadow-lg rounded-lg bg-white"
								id={headingId}
							>
								<Calendar
									initialDate={date ? new Date(date) : undefined}
									onDateChange={handleDateChange}
									minDate={min ? new Date(min) : undefined}
									maxDate={max ? new Date(max) : undefined}
									allowOtherMonthSelection={allowOtherMonthSelection}
									locale={locale}
								/>
							</motion.div>
						</AnimatePresence>
					</div>
				</FloatingPortal>
			)}
		</div>
	);
}
